// filepath: apps/web/src/features/proc/FailedInstanceDashboard.tsx
//
// FR-UI-PROC-1 AC-2 / AC-3 / AC-4 / AC-5 — failed-instance dashboard
// body.
//
// Renders the failed-instance list, surfacing the full FR-OBS-6
// AC-2 entry shape on each row, with the FR-UI-CORE-10 5-state
// error contract applied to the list fetch.
//
// State classification (mirrors features/lists/EntityListPage.tsx):
//   - 401 / 403 → permission-denied
//   - 410 / 404 → stale-version (rare for the list endpoint but
//     classified defensively)
//   - 429 → rate-limit
//   - 4xx other / 5xx / network → fetch-failure
//   - 200 + empty instances[] → SUCCESSFUL empty result, rendered
//     as empty-state chrome (NOT a permission-denied banner —
//     would leak existence).
//
// On retry-step 409 `not_failed` (a row-level error, not a list
// error) the dashboard surfaces the FR-UI-CORE-10 stale-version
// state via the row component (FailedInstanceRow.onStaleVersion).

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { ApiError } from "@/definitions/types";
import { SkeletonLayout } from "@/design-system/error-states/SkeletonLayout";
import { FetchError } from "@/design-system/error-states/FetchError";
import { PermissionDenied } from "@/design-system/error-states/PermissionDenied";
import { StaleVersionError } from "@/design-system/error-states/StaleVersionError";
import { RateLimitBackpressure } from "@/design-system/error-states/RateLimitBackpressure";
import { FailedInstanceRow } from "./FailedInstanceRow";
import { useFailedInstances } from "./useFailedInstances";
import { classifyFailedInstanceError } from "./index-shared";
import { InterventionControl } from "./InterventionControl";
import type { FailedInstance, FailedInstanceStatus } from "./types";

export interface FailedInstanceDashboardProps {
  /**
   * Optional click-through to the trace viewer. When supplied,
   * each row's "View trace" affordance routes to the trace
   * viewer for the given instance id (parent supplies the
   * navigation).
   */
  onViewTrace?: (instanceId: string) => void;
  /**
   * FR-BPM-18: when true, the dashboard mounts the
   * <InterventionControl> per row, exposing the six authorized
   * intervention actions (skip / continue_from / repeat /
   * change_remaining_work / finish / cancel). The control is
   * gated on instance.status (only non-terminal states are
   * eligible) and authorization (the InterventionAuthorizer seam
   * returns HTTP 403 not_authorized for non-owners).
   */
  enableIntervention?: boolean;
}

/**
 * FailedInstanceDashboard — page body for the failed-instance
 * dashboard.
 *
 * The classification helper lives in `./index-shared` and is
 * re-exported through the public API. This file consumes the
 * function via direct import to avoid a barrel-import cycle.
 */

export function FailedInstanceDashboard({
  onViewTrace,
  enableIntervention = false,
}: FailedInstanceDashboardProps): React.ReactElement {
  const { t } = useTranslation();
  const { data, isLoading, error, refetch } = useFailedInstances();

  if (isLoading) {
    return (
      <section
        data-testid="failed-instance-dashboard--loading"
        aria-labelledby="failed-instance-dashboard-title"
        className="failed-instance-dashboard failed-instance-dashboard--loading"
      >
        <h2 id="failed-instance-dashboard-title" className="visually-hidden">
          {t("processes.failed.title", "Failed instances")}
        </h2>
        <SkeletonLayout />
      </section>
    );
  }

  if (error !== null) {
    const status = classifyFailedInstanceError(error);
    return (
      <ErrorSurface
        status={status}
        error={error}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  const instances: FailedInstance[] = data?.instances ?? [];

  if (instances.length === 0) {
    return (
      <section
        data-testid="failed-instance-dashboard--empty"
        aria-labelledby="failed-instance-dashboard-title"
        className="failed-instance-dashboard failed-instance-dashboard--empty"
      >
        <h2 id="failed-instance-dashboard-title" className="visually-hidden">
          {t("processes.failed.title", "Failed instances")}
        </h2>
        <p
          className="failed-instance-dashboard-empty-message"
          role="status"
          aria-live="polite"
          data-testid="failed-instance-dashboard-empty-message"
        >
          {t("failed_instance.empty", "No failed instances.")}
        </p>
      </section>
    );
  }

  return (
    <section
      data-testid="failed-instance-dashboard"
      aria-labelledby="failed-instance-dashboard-title"
      className="failed-instance-dashboard"
    >
      <h2 id="failed-instance-dashboard-title" className="visually-hidden">
        {t("processes.failed.title", "Failed instances")}
      </h2>
      <ul
        className="failed-instance-dashboard-list"
        data-testid="failed-instance-dashboard-list"
        aria-label={t(
          "failed_instance.list_aria_label",
          "Failed instances for this tenant",
        )}
      >
        {instances.map((instance) => (
          <li
            key={instance.id}
            data-testid="failed-instance-dashboard-list-item"
            className="failed-instance-dashboard-list-item"
          >
            <FailedInstanceRow
              instance={instance}
              onMutated={() => {
                void refetch();
              }}
              onViewTrace={onViewTrace}
              onStaleVersion={() => {
                void refetch();
              }}
            />
            {enableIntervention && (
              <InterventionControl instanceId={instance.id} />
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * ErrorSurface — shared renderer for the 4 error states (loading +
 * success are separate). Mirrors the ListErrorSurface pattern at
 * features/lists/EntityListPage.tsx:300-407.
 */
function ErrorSurface({
  status,
  error,
  onRetry,
}: {
  status: FailedInstanceStatus;
  error: ApiError;
  onRetry: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  if (status === "permission-denied") {
    return (
      <section
        data-testid="failed-instance-dashboard--permission-denied"
        className="failed-instance-dashboard failed-instance-dashboard--permission-denied"
        aria-labelledby="failed-instance-dashboard-title"
      >
        <h2 id="failed-instance-dashboard-title" className="visually-hidden">
          {t("processes.failed.title", "Failed instances")}
        </h2>
        <PermissionDenied />
      </section>
    );
  }
  if (status === "stale-version") {
    return (
      <section
        data-testid="failed-instance-dashboard--stale-version"
        className="failed-instance-dashboard failed-instance-dashboard--stale-version"
        aria-labelledby="failed-instance-dashboard-title"
      >
        <h2 id="failed-instance-dashboard-title" className="visually-hidden">
          {t("processes.failed.title", "Failed instances")}
        </h2>
        <StaleVersionError />
      </section>
    );
  }
  if (status === "rate-limit") {
    return (
      <section
        data-testid="failed-instance-dashboard--rate-limit"
        className="failed-instance-dashboard failed-instance-dashboard--rate-limit"
        aria-labelledby="failed-instance-dashboard-title"
      >
        <h2 id="failed-instance-dashboard-title" className="visually-hidden">
          {t("processes.failed.title", "Failed instances")}
        </h2>
        <RateLimitBackpressure retryAfterSeconds={error.retryAfterSeconds} />
      </section>
    );
  }
  // fetch-failure (default)
  return (
    <section
      data-testid="failed-instance-dashboard--fetch-failure"
      className="failed-instance-dashboard failed-instance-dashboard--fetch-failure"
      aria-labelledby="failed-instance-dashboard-title"
    >
      <h2 id="failed-instance-dashboard-title" className="visually-hidden">
        {t("processes.failed.title", "Failed instances")}
      </h2>
      <FetchError
        message={t(
          "failed_instance.state.fetch_failure",
          "Failed to load failed instances.",
        )}
        onRetry={onRetry}
      />
    </section>
  );
}
